import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Input, Modal, Select, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";

export interface MemberRechargeCustomer {
    name?: string;
    realName?: string;
    mobile?: string;
    code?: string;
    id?: number;
}

export interface MemberRechargeRow {
    id: number | string;
    customerInfo?: MemberRechargeCustomer;
    memberLevel?: number | string;
    memberDay?: number;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

export interface MemberRechargeQuery {
    page: number;
    size: number;
    id?: string;
    customerKeyWord?: string;
    memberLevel?: string;
    openMode?: string;
    activationCode?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface ActivationCodeInfo {
    level?: number | string;
    duration?: number;
    state?: number | string;
    hasBind?: number | boolean;
    customerInfo?: MemberRechargeCustomer;
}

export interface MemberRechargePageProps {
    loadRecords?: (query: MemberRechargeQuery) => Promise<{ list?: MemberRechargeRow[]; total?: number }>;
    lookupCode?: (code: string) => Promise<ActivationCodeInfo | undefined>;
    searchCustomers?: (searchParam: string) => Promise<MemberRechargeCustomerOption[]>;
    exchange?: (params: { code: string; customerId?: number }) => Promise<unknown>;
}

export interface MemberRechargeCustomerOption {
    id: number;
    label: string;
}

const defaultQuery: MemberRechargeQuery = { page: 1, size: 10 };
const memberLevelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员" };
const openModeLabels: Record<number, string> = { 1: "微信支付", 2: "支付宝", 3: "激活码", 4: "APPLE PAY" };

/** 兼容 Vue 接口的数字/布尔 hasBind，并为旧测试数据保留 customerInfo 兜底。 */
const isActivationCodeBound = (info: ActivationCodeInfo) =>
    info.hasBind === undefined ? Boolean(info.customerInfo?.id) : info.hasBind === true || Number(info.hasBind) === 1;

/** 会员充值记录沿用 Vue 的会员等级枚举，过滤掉非会员空值。 */
const formatMemberLevel = (value?: number | string) => memberLevelLabels[Number(value)] || DEFAULT_VALUE;

/** 会员账号沿用 Vue 的 name/realName/mobile/code 组合显示方式。 */
const formatCustomer = (customer?: MemberRechargeCustomer) => {
    if (!customer) return DEFAULT_VALUE;
    return (
        [customer.name, customer.realName, customer.mobile, customer.code].filter(Boolean).join("|") || DEFAULT_VALUE
    );
};

const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);
const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 金额保留 Vue 页面展示的两位小数。 */
const formatAmount = (value?: number) => (value == null ? DEFAULT_VALUE : Number(value).toFixed(2));

/** 复用 Vue `/admin/member-open-record/paging` 会员充值分页接口。 */
export async function loadMemberRechargeRecords(
    query: MemberRechargeQuery
): Promise<{ list?: MemberRechargeRow[]; total?: number }> {
    const response = await request.service.get("/admin/member-open-record/paging", { params: query });
    return response.data;
}

/** 复用 Vue 激活码详情接口，供兑换弹窗校验激活码状态。 */
export async function lookupMemberActivationCode(code: string): Promise<ActivationCodeInfo | undefined> {
    const response = await request.service.get(`/admin/activation/code/find/${code}`);
    return response.data;
}

/** 复用 Vue 未绑定激活码的关联账号搜索参数 `{ searchParam }`。 */
export async function searchMemberRechargeCustomers(searchParam: string): Promise<MemberRechargeCustomerOption[]> {
    const response = await request.service.get<Array<{ customer: MemberRechargeCustomer }>>(
        "/admin/customer/shipping/selector",
        { params: { searchParam } }
    );
    return response.data.map(item => ({ id: item.customer.id as number, label: formatCustomer(item.customer) }));
}

/** 复用 Vue 激活码兑换接口。 */
export async function exchangeMemberActivationCode(params: { code: string; customerId?: number }): Promise<unknown> {
    const response = await request.service.get("/admin/activation/code/exchange", { params });
    return response.data;
}

function ExchangeDialog({
    open,
    lookupCode,
    searchCustomers,
    exchange,
    onClose,
    onUpdated
}: {
    open: boolean;
    lookupCode: (code: string) => Promise<ActivationCodeInfo | undefined>;
    searchCustomers: (searchParam: string) => Promise<MemberRechargeCustomerOption[]>;
    exchange: (params: { code: string; customerId?: number }) => Promise<unknown>;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const [code, setCode] = useState("");
    const [codeInfo, setCodeInfo] = useState<ActivationCodeInfo>();
    const [customerOptions, setCustomerOptions] = useState<MemberRechargeCustomerOption[]>([]);
    const [customerId, setCustomerId] = useState<number>();
    const [customerKeyword, setCustomerKeyword] = useState("");
    const [customerSearchError, setCustomerSearchError] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCode("");
        setCodeInfo(undefined);
        setCustomerOptions([]);
        setCustomerId(undefined);
        setCustomerKeyword("");
        setCustomerSearchError("");
        setErrorMessage("");
    }, [open]);

    const handleCodeChange = async (value: string) => {
        setCode(value);
        setCodeInfo(undefined);
        setCustomerOptions([]);
        setCustomerId(undefined);
        setCustomerKeyword("");
        setCustomerSearchError("");
        setErrorMessage("");
        if (value.length !== 20 || !/^[A-Za-z0-9]+$/.test(value)) return;
        setLoading(true);
        try {
            const info = await lookupCode(value);
            setCodeInfo(info);
            if (!info) setErrorMessage("该激活码不存在");
            else if (Number(info.state) !== 1)
                setErrorMessage(Number(info.state) === 2 ? "该激活码已兑换" : "该激活码已过期");
            else if (isActivationCodeBound(info)) setCustomerId(info.customerInfo?.id);
        } catch {
            setErrorMessage("激活码查询失败");
        } finally {
            setLoading(false);
        }
    };

    /** 沿用 Vue 未绑定激活码的远程账号搜索，并清除已失效的选择。 */
    const handleCustomerSearch = async (value: string) => {
        setCustomerKeyword(value);
        setCustomerId(undefined);
        setCustomerSearchError("");
        if (!value.trim()) {
            setCustomerOptions([]);
            return;
        }
        try {
            setCustomerOptions(await searchCustomers(value.trim()));
        } catch {
            setCustomerOptions([]);
            setCustomerSearchError("关联账号搜索失败");
        }
    };

    const submit = async () => {
        if (!/^[A-Za-z0-9]{20}$/.test(code)) {
            setErrorMessage("只允许输入字母和数字，长度为20");
            return;
        }
        if (!codeInfo) {
            setErrorMessage("该激活码不存在");
            return;
        }
        if (Number(codeInfo.state) !== 1) {
            setErrorMessage(Number(codeInfo.state) === 2 ? "该激活码已兑换" : "该激活码已过期");
            return;
        }
        if (!customerId) {
            setErrorMessage("请选择关联账号");
            return;
        }
        try {
            await exchange({ code, customerId });
            onUpdated();
            onClose();
        } catch {
            setErrorMessage("兑换失败");
        }
    };

    return (
        <Modal open={open} title="兑换" footer={null} onCancel={onClose}>
            <Space direction="vertical" style={{ width: "100%" }}>
                <label>
                    激活码
                    <input
                        aria-label="激活码"
                        maxLength={20}
                        value={code}
                        onChange={event => void handleCodeChange(event.target.value)}
                    />
                </label>
                {loading ? <span>查询中...</span> : null}
                {codeInfo ? (
                    <div>
                        <span>等级：{formatMemberLevel(codeInfo.level)}</span>
                        <span> 时长：{codeInfo.duration || 0}天</span>
                    </div>
                ) : null}
                {codeInfo && Number(codeInfo.state) === 1 && !isActivationCodeBound(codeInfo) ? (
                    <div>
                        <label>
                            关联账号
                            <input
                                aria-label="关联账号"
                                value={customerKeyword}
                                onChange={event => void handleCustomerSearch(event.target.value)}
                            />
                        </label>
                        {customerOptions.map(option => (
                            <div
                                key={option.id}
                                role="option"
                                aria-selected={customerId === option.id}
                                onClick={() => {
                                    setCustomerId(option.id);
                                    setCustomerKeyword(option.label);
                                }}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                ) : null}
                {customerSearchError ? <Alert message={customerSearchError} role="alert" type="error" /> : null}
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={() => void submit()}>
                        提交兑换
                    </Button>
                </Space>
            </Space>
        </Modal>
    );
}

export function MemberRechargePage({
    loadRecords = loadMemberRechargeRecords,
    lookupCode = lookupMemberActivationCode,
    searchCustomers = searchMemberRechargeCustomers,
    exchange = exchangeMemberActivationCode
}: MemberRechargePageProps) {
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<MemberRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<MemberRechargeQuery>(defaultQuery);
    const [form, setForm] = useState<Omit<MemberRechargeQuery, "page" | "size" | "sortKey" | "sortOrder">>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [exchangeOpen, setExchangeOpen] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("会员充值记录加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    const reload = () => setQuery(current => ({ ...current }));
    const submitFilters = () => setQuery({ page: 1, size: query.size, ...form });
    const resetFilters = () => {
        setForm({});
        setQuery(current => ({ page: 1, size: current.size }));
    };
    // 保留 Vue ProTable 的创建时间排序参数，zhihao-ui 的方向映射为旧接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 会员充值 ProTable 的字段和格式化逻辑。
    const columns = useMemo<TableColumnProps<MemberRechargeRow>[]>(
        () => [
            { key: "id", label: "编号", width: 138, prop: "id" },
            {
                key: "customerInfo",
                label: "账号",
                minWidth: 228,
                prop: "customerInfo",
                render: ({ row }) => formatCustomer(row.customerInfo)
            },
            {
                key: "memberLevel",
                label: "等级",
                width: 130,
                prop: "memberLevel",
                render: ({ row }) => formatMemberLevel(row.memberLevel)
            },
            {
                key: "memberDay",
                label: "时长",
                width: 100,
                prop: "memberDay",
                render: ({ row }) => `${row.memberDay || 0}天`
            },
            {
                key: "openMode",
                label: "支付/兑换方式",
                width: 150,
                prop: "openMode",
                render: ({ row }) => openModeLabels[Number(row.openMode)] || DEFAULT_VALUE
            },
            { key: "activationCode", label: "激活码", minWidth: 148, prop: "activationCode" },
            {
                key: "amount",
                label: "实收金额（元）",
                minWidth: 130,
                prop: "amount",
                align: "right",
                headerAlign: "right",
                render: ({ row }) => formatAmount(row.amount)
            },
            {
                key: "activationDate",
                label: "激活日期",
                width: 110,
                prop: "activationDate",
                render: ({ row }) => formatDateOnly(row.activationDate)
            },
            {
                key: "expireDate",
                label: "到期日期",
                width: 110,
                prop: "expireDate",
                render: ({ row }) => formatDateOnly(row.expireDate)
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 150,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDateTime(row.createdTime)
            }
        ],
        []
    );

    return (
        <section className="member-recharge-page flex h-full min-h-full flex-col" aria-label="member-recharge-page">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<MemberRechargeRow>
                    rowKey="id"
                    data={records}
                    columns={columns}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                会员充值记录
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space wrap className="admin-table-filter-form mb-4">
                                <Permission anyOf={["ROLE_ORDER_W"]}>
                                    <Button
                                        aria-label="兑换"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => setExchangeOpen(true)}
                                    >
                                        兑换
                                    </Button>
                                </Permission>
                                <label>
                                    编号
                                    <Input
                                        aria-label="编号"
                                        maxLength={50}
                                        placeholder="请输入编号搜索"
                                        value={form.id || ""}
                                        onChange={event => setForm({ ...form, id: event.target.value })}
                                    />
                                </label>
                                <label>
                                    账号信息
                                    <Input
                                        aria-label="账号信息"
                                        maxLength={50}
                                        placeholder="请输入账号信息搜索"
                                        value={form.customerKeyWord || ""}
                                        onChange={event => setForm({ ...form, customerKeyWord: event.target.value })}
                                    />
                                </label>
                                <label>
                                    会员等级
                                    <Select
                                        allowClear
                                        aria-label="会员等级"
                                        options={Object.entries(memberLevelLabels).map(([value, label]) => ({
                                            label,
                                            value
                                        }))}
                                        placeholder="请选择会员等级"
                                        value={form.memberLevel}
                                        onChange={value => setForm({ ...form, memberLevel: value })}
                                    />
                                </label>
                                <label>
                                    支付/兑换方式
                                    <Select
                                        allowClear
                                        aria-label="支付/兑换方式"
                                        options={Object.entries(openModeLabels).map(([value, label]) => ({
                                            label,
                                            value
                                        }))}
                                        placeholder="请选择支付方式"
                                        value={form.openMode}
                                        onChange={value => setForm({ ...form, openMode: value })}
                                    />
                                </label>
                                <label>
                                    激活码搜索
                                    <Input
                                        aria-label="激活码搜索"
                                        placeholder="请输入激活码搜索"
                                        value={form.activationCode || ""}
                                        onChange={event => setForm({ ...form, activationCode: event.target.value })}
                                    />
                                </label>
                                <Button
                                    aria-label="查询"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={submitFilters}
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ExchangeDialog
                open={exchangeOpen}
                lookupCode={lookupCode}
                searchCustomers={searchCustomers}
                exchange={exchange}
                onClose={() => setExchangeOpen(false)}
                onUpdated={reload}
            />
        </section>
    );
}
